import { useState, type FormEvent } from 'react';
import { useClientFormAction, useConfig } from '../../api/queries';
import { useToast } from '../../components/Toast';
import { api, errorMessage } from '../../lib/api';
import type { ClientFormDetail } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';

function defaultMessage(cf: ClientFormDetail) {
  return `Please take a few minutes to complete the "${cf.form.name}". Your progress is saved automatically, so you can come back to it at any time.`;
}

/** Copy / email / WhatsApp the client's personal link. */
export function SharePanel({ clientForm: cf }: { clientForm: ClientFormDetail }) {
  const { data: config } = useConfig();
  const toast = useToast();
  const [showEmail, setShowEmail] = useState(false);
  const [to, setTo] = useState(cf.clientEmail ?? '');
  const [message, setMessage] = useState(() => defaultMessage(cf));

  const send = useClientFormAction(cf.id, (input: { to: string; message: string }) =>
    api.post<{ clientForm: ClientFormDetail }>(`/client-forms/${cf.id}/send`, input),
  );

  const shareText = `Hello ${cf.clientName},\n\n${message}\n\n${cf.url}`;
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(cf.form.name)}&body=${encodeURIComponent(shareText)}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(cf.url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy automatically — select the link and copy it');
    }
  }

  function handleSend(e: FormEvent) {
    e.preventDefault();
    send.mutate(
      { to, message },
      {
        onSuccess: () => {
          toast.success(`Email sent to ${to}`);
          setShowEmail(false);
        },
        onError: (err) => toast.error(errorMessage(err)),
      },
    );
  }

  return (
    <section className="card share-panel">
      <h2>Share with {cf.clientName}</h2>
      {!cf.isActive && (
        <div className="alert alert-warning">This link is disabled. Enable it before sharing.</div>
      )}
      <div className="share-link">
        <input
          className="input"
          readOnly
          value={cf.url}
          onFocus={(e) => e.target.select()}
          aria-label="Client link"
        />
        <button className="btn btn-primary" onClick={() => void copy()}>
          Copy link
        </button>
      </div>
      <div className="row wrap">
        <button className="btn btn-secondary btn-sm" onClick={() => setShowEmail((v) => !v)}>
          ✉ Send by email
        </button>
        <a className="btn btn-secondary btn-sm" href={whatsapp} target="_blank" rel="noreferrer">
          WhatsApp
        </a>
        <a className="btn btn-ghost btn-sm" href={cf.url} target="_blank" rel="noreferrer">
          Open form ↗
        </a>
      </div>
      {cf.lastSentAt && <p className="muted small">Last emailed {formatDateTime(cf.lastSentAt)}</p>}

      {showEmail && (
        <form className="email-form" onSubmit={handleSend}>
          <label className="field">
            <span>To</span>
            <input
              className="input"
              type="email"
              required
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Message</span>
            <textarea
              className="textarea"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <p className="muted small">The link is added automatically below your message.</p>
          <div className="row wrap">
            {config?.emailEnabled ? (
              <button className="btn btn-primary btn-sm" disabled={send.isPending}>
                {send.isPending ? 'Sending…' : 'Send email'}
              </button>
            ) : (
              <span className="muted small">Server email (SMTP) is not configured —</span>
            )}
            <a className="btn btn-secondary btn-sm" href={mailto}>
              Open in my email app
            </a>
          </div>
        </form>
      )}
    </section>
  );
}
